// useSSE.ts
import { useState, useCallback, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import type { Attachment } from '../components/pages/Chat/Chat/types';
import { buildFreshAgentsHeaders } from '../utils/agentsRequest';

interface UseSSEOptions {
    method?: 'GET' | 'POST';
    body?: Record<string, unknown>;
}

export interface SSEData {
    message?: string;
    content?: string;
    event: string;
    error?: string;
    stream_id?: string;
    metadata?: {
        conversation_id: string;
        response_time_ms: number;
        total_agents_invoked: number;
        status: string;
        attachments?: Attachment[];
    };
}

const AGENTS_API_URL = process.env['NX_AGENTS_API_URL'];

export function useSSE(url: string) {
    const [data, setData] = useState<SSEData | null>(null);
    const [error, setError] = useState(false);
    const [isStreaming, setIsStreaming] = useState(false);
    const abortControllerRef = useRef<AbortController | null>(null);
    const streamIdRef = useRef<string | null>(null);
    const pendingCancelRef = useRef(false);

    const postCancel = useCallback(
        async (sid: string) => {
            try {
                await fetch(`${AGENTS_API_URL}${url}/${sid}/cancel`, {
                    method: 'POST',
                    headers: await buildFreshAgentsHeaders({ contentType: 'application/json' }),
                });
            } catch {
                // 404 = stream already finished, idempotent
            }
        },
        [url],
    );

    const cancelStream = useCallback(async () => {
        setIsStreaming(false);

        const sid = streamIdRef.current;
        if (!sid) {
            // Keep the request alive until the initial response reveals X-Stream-Id.
            pendingCancelRef.current = true;
            return;
        }

        abortControllerRef.current?.abort();
        streamIdRef.current = null;
        pendingCancelRef.current = false;
        await postCancel(sid);
    }, [postCancel]);

    console.log(data);

    const startStream = useCallback(
        async (options?: UseSSEOptions & { onMessage?: (data: SSEData) => void }) => {
            try {
                const controller = new AbortController();
                abortControllerRef.current = controller;
                // Create a cancel token up front so the backend can receive /cancel
                // even before it exposes an X-Stream-Id header.
                streamIdRef.current = uuidv4();
                pendingCancelRef.current = false;
                setIsStreaming(true);
                setError(false);
                setData(null);

                const headers: HeadersInit = await buildFreshAgentsHeaders({
                    accept: 'text/event-stream',
                    contentType: 'application/json',
                });

                const fetchOptions: RequestInit = {
                    method: options?.method || 'POST',
                    headers,
                    signal: controller.signal,
                };

                if (options?.body) {
                    fetchOptions.body = JSON.stringify({
                        ...options.body,
                        stream_id: streamIdRef.current,
                    });
                }

                const response = await fetch(AGENTS_API_URL + url, fetchOptions);

                if (!response.ok) {
                    throw new Error(`SSE connection failed: ${response.status}`);
                }

                const headerStreamId = response.headers.get('X-Stream-Id') || response.headers.get('x-stream-id');
                if (headerStreamId) {
                    streamIdRef.current = headerStreamId;
                    if (pendingCancelRef.current) {
                        pendingCancelRef.current = false;
                        abortControllerRef.current?.abort();
                        void postCancel(headerStreamId);
                    }
                }

                const reader = response.body?.getReader();
                const decoder = new TextDecoder();
                let currentEvent = '';
                let buffer = '';

                while (reader) {
                    const { done, value } = await reader.read();

                    if (done) {
                        break;
                    }

                    buffer += decoder.decode(value, { stream: true });
                    const lines = buffer.split('\n');
                    // Keep the last partial line in the buffer
                    buffer = lines.pop() || '';

                    for (const line of lines) {
                        const trimmedLine = line.trim();
                        if (!trimmedLine) continue;

                        if (trimmedLine.startsWith('event:')) {
                            currentEvent = trimmedLine.slice(6).trim();
                        } else if (trimmedLine.startsWith('data:')) {
                            const jsonData = trimmedLine.slice(5).trim();
                            if (jsonData) {
                                try {
                                    const parsedData = JSON.parse(jsonData) as Omit<SSEData, 'event'>;
                                    const fullData = {
                                        ...parsedData,
                                        event: currentEvent,
                                    };

                                    if (parsedData.stream_id && !streamIdRef.current) {
                                        streamIdRef.current = parsedData.stream_id;
                                        if (pendingCancelRef.current) {
                                            pendingCancelRef.current = false;
                                            abortControllerRef.current?.abort();
                                            void postCancel(parsedData.stream_id);
                                        }
                                    }

                                    // If callback is provided, use it
                                    if (options?.onMessage) {
                                        options.onMessage(fullData);
                                    }

                                    // Always update state for other consumers or if no callback
                                    setData(fullData);
                                } catch (e) {
                                    console.error('Error parsing SSE data:', e);
                                }
                            }
                        }
                    }
                }

                setIsStreaming(false);
                streamIdRef.current = null;
            } catch (err) {
                if (err instanceof DOMException && err.name === 'AbortError') {
                    // Ignore abort errors
                } else {
                    console.error('SSE Error:', err);
                    setError(true);
                }
                setIsStreaming(false);
                streamIdRef.current = null;
            }
        },
        [postCancel, url],
    );

    const clearData = useCallback(() => {
        setData(null);
    }, []);

    return { data, error, isStreaming, startStream, clearData, cancelStream };
}
